import EAButton from 'components/common/EAButton';
import EAAlert from 'components/EAAlert';
import i18next from 'i18next';
import { useContext, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { isAbortError } from 'services/common/ApiUtils';
import { ERROR, SUCCESS } from 'services/common/Constants';
import { getEcovadisThirdpartyIntegrationID, handleEcovadisApiCallError, isMultipleSuppliersError, splitMultipleSuppliers } from 'services/common/EcovadisUtils';
import {
  fetchExistingEvaluation,
  verifyEvDataExistsForThirdparty,
} from 'services/edge/EcovadisService';
import { Context } from 'states/Store';
import useStyles from 'style/js-style/components/thirdparties/ecovadis/EcovadisStyle';
import EcovadisFoundSupplierDialog from './EcovadisDialogs/EcovadisFoundSupplier/EcovadisFoundSupplierDialog';

const EcovadisErrorBloc = ({ evData, onRiskChange = () => { } }) => {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [foundSupplier, setFoundSupplier] = useState();
  const [isFoundSupplierDialogOpen, setIsFoundSupplierDialogOpen] = useState(
    false
  );

  const abortCtrlRef = useRef();

  const formMethods = useForm({
    mode: 'onChange',
    delayError: 500,
  });

  const {
    handleSubmit,
  } = formMethods;

  const fetchAndUpdateThirdpartyEcovadisData = async (data) => {
    try {
      await fetchExistingEvaluation(
        state.account.id,
        state.thirdparty.thirdpartyId,
        data,
        dispatch
      );
      dispatch({
        type: 'ALERT',
        alert: {
          type: SUCCESS,
          msg: i18next.t('ecovadis.messages.errorUpdateSuccess'),
        },
      });
      onRiskChange();
    } catch (error) {
      handleEcovadisApiCallError(error, state.thirdparty.thirdpartyId, dispatch);
    }
  };

  const onErrorCorrectionClick = async () => {
    try {
      const evDataResult = await verifyAndGetEvDataForThirdparty();

      // If we get a result post verification, it means that we are not sure and need the user to confirm
      if (evDataResult) {
        setFoundSupplier(evDataResult);
        setIsFoundSupplierDialogOpen(true);
      } else {
        // If there is no result we may proceed to fetch and update ecovadis data
        setFoundSupplier(null);
        await fetchAndUpdateThirdpartyEcovadisData();
      }
    } catch (error) {
      if (!isAbortError(error)) {
        handleEcovadisApiCallError(error, state.thirdparty.thirdpartyId, dispatch);
      }
    }
  }

  const validateFoundSupplier = async () => {
    try {
      await fetchAndUpdateThirdpartyEcovadisData();
    } catch (error) {
      handleEcovadisApiCallError(error, state.thirdparty.thirdpartyId, dispatch);
    }
  }

  const verifyAndGetEvDataForThirdparty = async () => {

    if (abortCtrlRef.current) {
      abortCtrlRef.current.abort();
    }

    abortCtrlRef.current = new AbortController();
    const signal = abortCtrlRef.current.signal;

    const result = await verifyEvDataExistsForThirdparty(
      state.account.id,
      state.thirdparty.thirdpartyId,
      null,
      signal
    );

    return result.content;
  };

  const onFoundSupplierDialogValidation = async () => {
    setIsFoundSupplierDialogOpen(false);
    await fetchAndUpdateThirdpartyEcovadisData();
  };

  return (
    <div className={classes.uniqueIdErrorWrapper}>
      <EAAlert severity={ERROR} closable={false}>
        <div className={classes.uniqueIdErrorAlertMsg}>
          {i18next.t(`ecovadis.messages.errors.${evData.error}`, {integrationId: getEcovadisThirdpartyIntegrationID(state.thirdparty.thirdpartyId)})}
        </div>
        <div>

          {isMultipleSuppliersError(evData) && (
            <>
              {i18next.t("ecovadis.messages.concernedThirdparties")}
              <ul>
                {splitMultipleSuppliers(evData)?.map(supplierName => <li key={supplierName}>{supplierName}</li>)}
              </ul>
            </>
          )}
        </div>
      </EAAlert>
      <FormProvider {...formMethods}>
        <form
          id="found-supplier-form"
          onSubmit={handleSubmit(validateFoundSupplier)}
        >
          <EAButton
            onClick={(e) => {
              e.preventDefault();
              onErrorCorrectionClick();
            }}
            content={i18next.t('refresh')}
            colorVariant={'primary'}
          />
          {isFoundSupplierDialogOpen && (
            <EcovadisFoundSupplierDialog
              isOpen={isFoundSupplierDialogOpen}
              onClose={() => setIsFoundSupplierDialogOpen(false)}
              onValidate={onFoundSupplierDialogValidation}
              supplier={foundSupplier}
              dialogTitle={i18next.t('ecovadis.foundSupplier.title')}
            />
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default EcovadisErrorBloc;
